import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, FileDown, FileSpreadsheet, Loader2, PencilLine, Save, X } from 'lucide-react';
import type { GbuVoll, Kataster, MassnahmeEntwurf, MassnahmeListenZeile, SifaGbu, SifaGbuMassnahme } from '../../../services/sifa/gbuTypes';
import { massnahmenListe } from '../../../services/sifa/gbuLogic';
import { ladeGbu, speichereMassnahme } from '../../../services/sifa/gbuService';
import { describeError } from '../../../services/errorMessage';
import { rechtFehlerOder } from '../../../services/rechte/rechtFehler';
import { massnahmenlisteAusgeben } from './gbuAusgabe';
import {
  MASSNAHMEN_FILTER,
  MASSNAHMEN_FILTER_LABEL,
  MASSNAHME_ART_TON,
  MassnahmenFilter,
  STAND_LABEL,
  STAND_TON,
  filtereMassnahmen,
  gbusFuerAuswertung,
  istIsoDatum,
  mitStandFeldern,
  massnahmeEntwurfAus,
  sortiereMassnahmen,
  zaehleMassnahmen,
  formatTag,
} from './gbuModulLogic';

interface Props {
  gbus: SifaGbu[];
  kataster: Kataster;
  kundeName: string;
  bueroName: string;
  heute: string;
  isReadOnly: boolean;
  isMobile: boolean;
  onOeffnen: (gbuId: string) => void;
}

/**
 * Massnahmen- und Terminliste ueber alle GBUs (Plan §4.5) — eine Auswertung,
 * kein eigenes Dokument. Grundlage sind die freigegebenen Beurteilungen; auf
 * Wunsch auch Entwuerfe. Ausgabe als PDF oder Excel (A4 quer) mit dem
 * gewaehlten Filter.
 *
 * Umsetzung und Wirksamkeit lassen sich hier vermerken — die einzigen
 * Felder, die nach der Freigabe noch geschrieben werden (gbuService,
 * Riegel c). Erst die Wirksamkeitspruefung schliesst eine Massnahme.
 */
export const MassnahmenAnsicht: React.FC<Props> = ({ gbus, kataster, kundeName, bueroName, heute, isReadOnly, isMobile, onOeffnen }) => {
  const [volle, setVolle] = useState<GbuVoll[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [mitEntwuerfen, setMitEntwuerfen] = useState(false);
  const [filter, setFilter] = useState<MassnahmenFilter>('offen');
  const [ausgabe, setAusgabe] = useState<'pdf' | 'xlsx' | null>(null);
  const [standFuer, setStandFuer] = useState<{ voll: GbuVoll; massnahme: SifaGbuMassnahme } | null>(null);
  const [lauf, setLauf] = useState(0);

  // Alle nicht archivierten einmal laden; der Schalter „mit Entwuerfen" filtert nur.
  const ladeIds = useMemo(() => gbusFuerAuswertung(gbus, true).map((g) => g.id).join('|'), [gbus]);
  useEffect(() => {
    let aktiv = true;
    const ids = ladeIds ? ladeIds.split('|') : [];
    Promise.allSettled(ids.map((id) => ladeGbu(id)))
      .then((erg) => {
        if (!aktiv) return;
        setVolle(erg.flatMap((e) => (e.status === 'fulfilled' ? [e.value] : [])));
        const fehlend = erg.filter((e) => e.status === 'rejected');
        setFehler(fehlend.length ? `${fehlend.length} Beurteilung(en) ließen sich nicht laden: ${describeError((fehlend[0] as PromiseRejectedResult).reason)}` : null);
        setLaden(false);
      });
    return () => { aktiv = false; };
  }, [ladeIds, lauf]);

  // Volle Staende MIT Fassungen: bei einer Fortschreibung liefert massnahmenListe die Massnahmen der
  // gueltigen Fassung (Stand per Id aus den Live-Zeilen); nie freigegebene Entwuerfe nur mit dem Schalter.
  const alleZeilen = useMemo(
    () => massnahmenListe(volle, kataster, heute, { mitEntwuerfen }),
    [volle, kataster, heute, mitEntwuerfen],
  );
  const zaehler = zaehleMassnahmen(alleZeilen);
  const sichtbar = useMemo(() => sortiereMassnahmen(filtereMassnahmen(alleZeilen, filter)), [alleZeilen, filter]);

  const finde = (z: MassnahmeListenZeile) => {
    const voll = volle.find((v) => v.gbu.id === z.gbuId);
    const massnahme = voll?.massnahmen.find((m) => m.id === z.massnahmeId);
    return voll && massnahme ? { voll, massnahme } : null;
  };

  const ausgeben = async (endung: 'pdf' | 'xlsx') => {
    if (ausgabe) return;
    setAusgabe(endung);
    try {
      await massnahmenlisteAusgeben(sichtbar, {
        kundeName,
        bueroName,
        stand: heute,
        untertitel: `${MASSNAHMEN_FILTER_LABEL[filter]}${mitEntwuerfen ? ' · mit Entwürfen' : ''}`,
      }, endung);
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setAusgabe(null);
    }
  };

  const termin = (z: MassnahmeListenZeile) => (
    <span className={z.ueberfaellig ? 'text-red-700 font-bold inline-flex items-center gap-1' : 'text-slate-600'}>
      {z.ueberfaellig && <CalendarClock className="w-3.5 h-3.5" />}
      {z.termin ? formatTag(z.termin) : 'laufend'}
    </span>
  );
  const standChip = (z: MassnahmeListenZeile) => (
    <span className={`inline-flex px-2 py-0.5 text-[11px] font-bold border rounded-full whitespace-nowrap ${STAND_TON[z.stand]}`}>{STAND_LABEL[z.stand]}</span>
  );
  const standKnopf = (z: MassnahmeListenZeile) => {
    if (isReadOnly) return null;
    const ziel = finde(z);
    if (!ziel || ziel.voll.gbu.status === 'archiviert') return null;
    return (
      <button onClick={() => setStandFuer(ziel)} title="Umsetzung und Wirksamkeit" className="p-2 min-h-[36px] text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg">
        <PencilLine className="w-4 h-4" />
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Maßnahmen- und Terminliste</h2>
          <p className="text-xs text-slate-500">
            {zaehler.alle} Maßnahmen · {zaehler.offen} offen
            {zaehler.ueberfaellig > 0 && <span className="text-red-600 font-bold"> · {zaehler.ueberfaellig} überfällig</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => void ausgeben('pdf')} disabled={!!ausgabe || laden || sichtbar.length === 0} className="min-h-[44px] px-3 flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl disabled:opacity-40">
            {ausgabe === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} PDF
          </button>
          <button onClick={() => void ausgeben('xlsx')} disabled={!!ausgabe || laden || sichtbar.length === 0} className="min-h-[44px] px-3 flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl disabled:opacity-40">
            {ausgabe === 'xlsx' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {MASSNAHMEN_FILTER.map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`min-h-[36px] px-3 text-xs font-bold rounded-full border ${filter === f ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
            {MASSNAHMEN_FILTER_LABEL[f]} ({zaehler[f]})
          </button>
        ))}
        <label className="ml-auto text-xs text-slate-600 flex items-center gap-1.5 min-h-[36px]">
          <input type="checkbox" checked={mitEntwuerfen} onChange={(e) => setMitEntwuerfen(e.target.checked)} /> auch Entwürfe
        </label>
      </div>

      {fehler && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span className="flex-1">{fehler}</span>
          <button onClick={() => { setLaden(true); setLauf((n) => n + 1); }} className="font-bold underline">Erneut laden</button>
        </div>
      )}

      {laden ? (
        <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
      ) : sichtbar.length === 0 ? (
        <div className="p-6 bg-white border border-slate-200 rounded-2xl text-center text-xs text-slate-500">
          {alleZeilen.length === 0
            ? (mitEntwuerfen ? 'Noch keine Maßnahme erfasst.' : 'Noch keine freigegebene Beurteilung mit Maßnahmen — „auch Entwürfe" zeigt die Entwürfe.')
            : `Keine Maßnahme in „${MASSNAHMEN_FILTER_LABEL[filter]}".`}
        </div>
      ) : isMobile ? (
        <div className="space-y-2">
          {sichtbar.map((z) => (
            <div key={z.massnahmeId} className="bg-white border border-slate-200 rounded-2xl p-3 space-y-1.5">
              <div className="flex items-start gap-2">
                <span className={`shrink-0 px-1.5 text-[11px] font-black rounded ${MASSNAHME_ART_TON[z.art]}`}>{z.art}</span>
                <p className="flex-1 text-sm text-slate-800">{z.text}</p>
                {standKnopf(z)}
              </div>
              <p className="text-[11px] text-slate-500">
                <button onClick={() => onOeffnen(z.gbuId)} className="font-bold underline">GBU Nr. {z.gbuNummer}</button> · {z.bereich} · {z.gefaehrdung}
              </p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {standChip(z)} {termin(z)} {z.verantwortlich && <span className="text-slate-500">· {z.verantwortlich}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">GBU</th>
                <th className="px-3 py-2 text-left">Gefährdung</th>
                <th className="px-3 py-2 text-left">Maßnahme</th>
                <th className="px-3 py-2 text-left">Verantwortlich</th>
                <th className="px-3 py-2 text-left">Termin</th>
                <th className="px-3 py-2 text-left">Stand</th>
                <th className="px-1 py-2 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sichtbar.map((z) => (
                <tr key={z.massnahmeId} className="align-top">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button onClick={() => onOeffnen(z.gbuId)} className="font-bold text-amber-700 hover:underline">Nr. {z.gbuNummer}</button>
                    <p className="text-[11px] text-slate-500">{z.bereich}</p>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 max-w-[16rem]">{z.gefaehrdung}</td>
                  <td className="px-3 py-2">
                    <span className={`mr-1.5 px-1.5 text-[11px] font-black rounded ${MASSNAHME_ART_TON[z.art]}`}>{z.art}</span>
                    <span className="text-slate-800">{z.text}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{z.verantwortlich || '—'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{termin(z)}</td>
                  <td className="px-3 py-2">{standChip(z)}</td>
                  <td className="px-1 py-1">{standKnopf(z)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {standFuer && (
        <StandDialog
          voll={standFuer.voll}
          massnahme={standFuer.massnahme}
          heute={heute}
          onClose={() => setStandFuer(null)}
          onGespeichert={(m) => {
            setVolle((l) => l.map((v) => (v.gbu.id === m.gbuId ? { ...v, massnahmen: v.massnahmen.map((x) => (x.id === m.id ? m : x)) } : v)));
            setStandFuer(null);
          }}
        />
      )}
    </div>
  );
};

const FELD = 'w-full px-3 py-2 min-h-[44px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-amber-300';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';

/** Umsetzung und Wirksamkeit einer Massnahme vermerken (Innendienst). */
const StandDialog: React.FC<{
  voll: GbuVoll;
  massnahme: SifaGbuMassnahme;
  heute: string;
  onClose: () => void;
  onGespeichert: (m: SifaGbuMassnahme) => void;
}> = ({ voll, massnahme, heute, onClose, onGespeichert }) => {
  const [e, setE] = useState<MassnahmeEntwurf>(() => massnahmeEntwurfAus(massnahme));
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const setze = <K extends keyof MassnahmeEntwurf>(k: K, v: MassnahmeEntwurf[K]) => setE((p) => ({ ...p, [k]: v }));

  const speichern = async () => {
    if (speichert) return;
    for (const d of [e.umgesetztGemeldetAm, e.wirksamGeprueftAm]) {
      if ((d || '').trim() && !istIsoDatum(d)) { setFehler('Bitte gültige Daten eingeben.'); return; }
    }
    if (e.wirksam !== null && !(e.wirksamGeprueftAm || '').trim()) { setFehler('Zur Wirksamkeit gehört das Datum der Prüfung.'); return; }
    setSpeichert(true);
    setFehler(null);
    try {
      // Frisch lesen: im Entwurf schreibt der Dienst ALLE Felder — ein alter Stand von Text, Termin
      // oder Art (inzwischen im Editor geaendert) darf nicht zurueckgeschrieben werden.
      const frisch = await ladeGbu(voll.gbu.id);
      const zeile = frisch.massnahmen.find((x) => x.id === massnahme.id);
      if (!zeile) throw new Error('Diese Maßnahme gibt es im aktuellen Stand nicht mehr — bitte die Liste neu laden.');
      const m = await speichereMassnahme(frisch.gbu, zeile.gefaehrdungId, mitStandFeldern(massnahmeEntwurfAus(zeile), e), zeile.id);
      onGespeichert(m);
    } catch (err) {
      setFehler(rechtFehlerOder(err));
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={speichert ? undefined : onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[95vh] flex flex-col" onClick={(ev) => ev.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800">Umsetzung und Wirksamkeit</h3>
            <p className="text-xs text-slate-500 truncate">GBU Nr. {voll.gbu.nummer} · {massnahme.art} – {massnahme.text}</p>
          </div>
          <button onClick={onClose} disabled={speichert} className="p-2 hover:bg-slate-100 rounded-full disabled:opacity-40"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={LABEL}>Umgesetzt gemeldet am</span>
              <input type="date" max={heute} value={e.umgesetztGemeldetAm ?? ''} onChange={(ev) => setze('umgesetztGemeldetAm', ev.target.value || null)} className={FELD} />
            </label>
            <label className="block">
              <span className={LABEL}>Gemeldet von</span>
              <input value={e.umgesetztGemeldetVon ?? ''} onChange={(ev) => setze('umgesetztGemeldetVon', ev.target.value)} className={FELD} placeholder="z. B. Bauleiter" />
            </label>
          </div>
          <label className="block">
            <span className={LABEL}>Kommentar zur Umsetzung</span>
            <input value={e.umgesetztKommentar ?? ''} onChange={(ev) => setze('umgesetztKommentar', ev.target.value)} className={FELD} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={LABEL}>Wirksamkeit geprüft am</span>
              <input type="date" max={heute} value={e.wirksamGeprueftAm ?? ''} onChange={(ev) => setze('wirksamGeprueftAm', ev.target.value || null)} className={FELD} />
            </label>
            <label className="block">
              <span className={LABEL}>Kürzel</span>
              <input value={e.wirksamKuerzel ?? ''} onChange={(ev) => setze('wirksamKuerzel', ev.target.value)} className={FELD} maxLength={20} />
            </label>
          </div>
          <label className="block">
            <span className={LABEL}>Ergebnis</span>
            <select value={e.wirksam === null ? '' : e.wirksam ? 'ja' : 'nein'} onChange={(ev) => setze('wirksam', ev.target.value === '' ? null : ev.target.value === 'ja')} className={FELD}>
              <option value="">— noch nicht geprüft —</option>
              <option value="ja">wirksam</option>
              <option value="nein">nicht wirksam</option>
            </select>
          </label>
          <label className="block">
            <span className={LABEL}>Notiz</span>
            <input value={e.wirksamNotiz ?? ''} onChange={(ev) => setze('wirksamNotiz', ev.target.value)} className={FELD} />
          </label>
          {fehler && <p className="text-xs text-red-700">{fehler}</p>}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} disabled={speichert} className="min-h-[44px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl disabled:opacity-40">Abbrechen</button>
          <button onClick={() => void speichern()} disabled={speichert} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl disabled:opacity-40">
            {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
          </button>
        </div>
      </div>
    </div>
  );
};
